/**
 * Static Tenant Configuration
 *
 * Centralized config for tenant-specific branding and settings.
 * Used as a FALLBACK when dynamic branding from the API is unavailable.
 *
 * Primary branding is fetched from the database via ISR (see lib/branding.ts).
 * This static config ensures pages render correctly during:
 * - Initial deployment before branding is configured
 * - API failures or network issues
 * - Build-time static generation
 *
 * Note: Logo/favicon URLs use placeholder CDN paths - update when CDN is configured.
 */

import { getFundraiserShortDomain } from '@impact/types';

export interface TenantConfig {
  slug: string;
  name: string;
  displayName: string;
  domain?: string;
  siteUrl: string;
  /**
   * Vanity/short domain used when displaying a fundraiser handle
   * (e.g. `gfolk.me/jane`). Display-only; falls back to `gfolk.me` where unset.
   * Values come from the shared `FUNDRAISER_SHORT_DOMAINS` map in
   * `@impact/types` so the API builds email links from the same source.
   */
  fundraiserShortDomain?: string;
  // CDN URLs for assets
  logoUrl: string;
  faviconUrl: string;
  heroImageUrl?: string;
  ogImageUrl?: string;
  ogDescription?: string;
  /**
   * Visual variant for donation surfaces (modal, amount card, amount selector).
   * `'light'` = white panels with `--primary` accent buttons.
   * `'dark'`  = stone-900 panels with brand-accent buttons (DGR default).
   * Unset → 'dark'.
   */
  donationTheme?: 'light' | 'dark';
  /**
   * Per-tenant override for the donation "impact" line shown beneath the donate
   * button on the amount card and donation modal. The shared i18n fallback
   * (`donate.form.impactMessage`) is DGR-specific ("100% goes to men's health
   * initiatives") and shouldn't bleed through for tenants with a different cause.
   * - `undefined` → fall back to the i18n string (DGR/DGD default).
   * - `null`      → render no impact line at all.
   * - `string`    → render this exact copy.
   */
  donationImpactMessage?: string | null;
  /**
   * Base for a team's short link, e.g. `gfolk.team/<handle>` or
   * `unitedweride.global/team/<handle>`. May include a path segment.
   * Display-only; falls back to `gfolk.team` where unset.
   */
  teamShortUrlBase?: string;
  /**
   * Vehicle category for this tenant's events. Drives shared entrant UI such
   * as the dashboard vehicle icon and "Riding/Driving since" copy.
   * Unset → 'motorcycle' (DGR/UWR default).
   */
  vehicleType?: 'car' | 'motorcycle';
  /**
   * Per-tenant meta tag overrides. Set these when en.json's `meta.*` keys
   * (which are DGR-specific) shouldn't bleed through for this tenant and the
   * API translation bundle doesn't yet have per-tenant overrides. When
   * present, these win over the i18n bundle in `generateMetadata`.
   */
  meta?: {
    siteDescription?: string;
    ogTitle?: string;
    ogDescription?: string;
  };
}

// Derive tenant slugs from config keys
export const TENANT_SLUGS = ['dgr', 'dgd', 'uwr', 'base'] as const;
export type TenantSlug = (typeof TENANT_SLUGS)[number];

export function isTenantSlug(slug: string): slug is TenantSlug {
  return TENANT_SLUGS.includes(slug as TenantSlug);
}

export const TENANT_CONFIGS: Record<string, TenantConfig> = {
  dgr: {
    slug: 'dgr',
    name: 'dgr',
    displayName: "The Distinguished Gentleman's Ride",
    domain: 'gentlemansride.com',
    siteUrl: 'https://gentlemansride.com',
    fundraiserShortDomain: getFundraiserShortDomain('dgr'),
    logoUrl: '/images/dgr/DGRLogo.svg',
    faviconUrl: '/images/dgr/favicon/favicon-32x32.png',
    heroImageUrl: '/images/dgr/HeroImage.jpg',
    ogImageUrl: '/images/dgr/DGR2026_FB_SharingIMG.jpg',
    ogDescription:
      "Save the Date for The 2026 Distinguished Gentleman's Ride and join us on the 17th May 2026.",
  },
  dgd: {
    slug: 'dgd',
    name: 'dgd',
    displayName: "The Distinguished Gentleman's Drive",
    domain: 'gentlemansdrive.com',
    siteUrl: 'https://gentlemansdrive.com',
    fundraiserShortDomain: getFundraiserShortDomain('dgd'),
    teamShortUrlBase: 'gentdrive.me',
    vehicleType: 'car',
    logoUrl: '/images/dgd/DGD_Logo.svg',
    faviconUrl: '/images/dgd/favicon/favicon-32x32.png',
    heroImageUrl: '/images/dgd/DGD_HeroImage2.jpg',
    donationTheme: 'light',
    // ogImageUrl intentionally omitted until DGD2026 sharing image is provided
    meta: {
      siteDescription:
        "Save the date for The Distinguished Gentleman's Drive — Sunday 27th September 2026, worldwide.",
      ogTitle: "The Distinguished Gentleman's Drive 2026",
      ogDescription:
        "Sunday 27th September 2026. Classic cars and elegant style, driving for men's health worldwide.",
    },
  },
  uwr: {
    slug: 'uwr',
    name: 'uwr',
    displayName: 'United We Ride',
    domain: 'unitedweride.global',
    siteUrl: 'https://unitedweride.global',
    fundraiserShortDomain: getFundraiserShortDomain('uwr'),
    logoUrl: '/images/uwr/UWR_Logo_General.png',
    faviconUrl: '/images/uwr/favicon/favicon-32x32.png',
    heroImageUrl: '/images/uwr/HeroImageUWR.jpg',
    donationTheme: 'light',
    // UWR's cause is The Salvation Army, not men's health — and there's no
    // accurate "100% goes to…" claim to assert yet, so hide the impact line.
    donationImpactMessage: null,
    teamShortUrlBase: 'unitedweride.global/team',
    meta: {
      siteDescription:
        'Riding together across Australia & New Zealand on Saturday 12th September 2026 to support The Salvation Army.',
      ogTitle: 'United We Ride 2026',
      ogDescription:
        'Saturday 12th September 2026. Join the ride across Australia & New Zealand to support The Salvation Army.',
    },
  },
  base: {
    slug: 'base',
    name: 'base',
    displayName: 'Impact Platform',
    siteUrl: 'https://yourimpact.cc',
    logoUrl: '', // No logo for base platform
    faviconUrl: '',
  },
};

/**
 * Get tenant config by slug, falling back to base config
 */
export function getTenantConfig(slug: string | null | undefined): TenantConfig {
  if (!slug) {
    return TENANT_CONFIGS.base;
  }
  return TENANT_CONFIGS[slug] ?? TENANT_CONFIGS.base;
}

/**
 * Get tenant slug from tenant name
 * Tenant names are lowercase slugs (e.g., 'dgr', 'dgd')
 */
export function getTenantSlugFromName(name: string | null | undefined): string | null {
  if (!name) return null;
  const normalizedName = name.toLowerCase();
  return TENANT_CONFIGS[normalizedName] ? normalizedName : null;
}
